import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';

import SubscriptionCheckoutModal, { type SubscriptionPaymentSubmission } from '../components/SubscriptionCheckoutModal';
import type { Workspace, WorkspaceScope } from '../components/screens/RunScreens';
import { getCodebaseZipValidationError, type LoadState } from '../components/workflowShared';
import { RunGitHubLoginModal } from '../run/RunGitHubAccount';
import AppChatView, { type AppChatSuggestion } from './app/AppChatView';
import {
  AppConnectGitHubWidget,
  AppPentestProgressWidget,
  AppPlansWidget,
  AppRepoPickerWidget,
  AppRunPentestWidget
} from './app/AppChatWidgets';
import AppContextView from './app/AppContextView';
import AppIssuePanel from './app/AppIssuePanel';
import AppLineup from './app/AppLineup';
import AppIssuesView from './app/AppIssuesView';
import AppPricingView from './app/AppPricingView';
import AppScopeView from './app/AppScopeView';
import AppSettingsView from './app/AppSettingsView';
import AppSidebar, { type AppView } from './app/AppSidebar';
import AppToolsView from './app/AppToolsView';
import AppWorkflowsView from './app/AppWorkflowsView';
import {
  AppChatReportedError,
  detectScanIntent,
  findEmailAddress,
  getPendingEmailRequest,
  useAppChats,
  type AppChat,
  type AppChatMessage,
  type AppEmailRequestWidget,
  type AppIssueRef,
  type AppScanIntent,
  type AppWorkspaceResourcePointer
} from './app/appChatStore';
import {
  IconChevronRight,
  IconCompose,
  IconFileText,
  IconFix,
  IconIssues,
  IconScope,
  IconSidebar,
  IconTag,
  IconZap
} from './app/appIcons';
import {
  findReusableUploadedCodebase,
  isUploadedScanTarget,
  loadUploadedCodebaseDetails,
  loadRepositoryChoices,
  toScanTarget,
  toUploadedScanTarget,
  uploadCodebaseZip,
  type AppPentestTarget,
  type AppRepositoryChoice
} from './app/appScanApi';
import { deletePendingZip, loadPendingZip, storePendingZip } from './app/appUploadStore';
import {
  getWorkspacePath,
  getWorkspaceRootPath,
  parseWorkspaceRoot,
  type AppWorkspaceRoot
} from './app/appWorkspaceRoot';
import {
  APP_PLANS,
  cancelCurrentSubscription,
  checkoutSubscription,
  getAppPlan,
  useAppPlan,
  type AppPlan,
  type AppPlanId
} from './app/appPlans';
import {
  useSecurityOverview,
  type OverviewAction,
  type OverviewActionKind
} from './app/appOverview';
import { useWorkspaceContext } from './app/appContextStore';
import { useAppAccount } from './app/useAppAccount';
import { runIsDevGitHubAccountEnabled, runRequestJson } from './app/appRunData';
import { useWorkflows } from './app/appWorkflowStore';
import { useWorkspaceIssues, type RegisterIssue } from './app/useWorkspaceIssues';
import './AppPage.css';

const MOBILE_QUERY = '(max-width: 768px)';
// What the user asked for before a login or GitHub redirect, so it can continue afterwards.
const PENDING_STORAGE_KEY = 'havu-app-pending-v1';

type PendingAction =
  | { prompt: string }
  | { chatId: string; intent: AppScanIntent }
  | { uploadKey: string; prompt: string };

interface PendingSubscriptionCheckout {
  plan: AppPlan;
  isSubmitting: boolean;
  error: string;
}

interface SendOptions {
  /** The chat to append to; `null` starts a new one. */
  chatId: string | null;
  /** The application to send in; defaults to the current one. */
  workspaceId?: string;
  detectIntent?: boolean;
  issueRefs?: AppIssueRef[];
  resourceIds?: string[];
  title?: string;
}

interface RoutedWorkspaceResource extends AppWorkspaceResourcePointer {
  data?: Record<string, unknown>;
}

const ASSESSMENT_PROMPT = 'Run a security assessment';

const ASSESS_SUGGESTION: AppChatSuggestion = {
  label: 'Assess an application',
  icon: <IconZap aria-hidden={true} />,
  prompt: ASSESSMENT_PROMPT,
  mode: 'send'
};

// How many of Havu's next steps the empty chat offers.
const BRIEFING_ACTIONS = 3;

const ACTION_ICONS: Record<OverviewActionKind, ReactNode> = {
  setup: <IconScope aria-hidden={true} />,
  baseline: <IconZap aria-hidden={true} />,
  retry: <IconZap aria-hidden={true} />,
  fix: <IconFix aria-hidden={true} />,
  triage: <IconIssues aria-hidden={true} />,
  refresh: <IconZap aria-hidden={true} />,
  allowance: <IconTag aria-hidden={true} />
};

const REPORT_SUGGESTION: AppChatSuggestion = {
  label: 'Check a bug bounty or pentest report',
  icon: <IconFileText aria-hidden={true} />,
  prompt: 'Analyse the following bug bounty or pentest report:\n\n',
  mode: 'fill'
};

const QUESTIONNAIRE_SUGGESTION: AppChatSuggestion = {
  label: 'Answer a security questionnaire',
  icon: <IconIssues aria-hidden={true} />,
  prompt: 'Help me answer this security questionnaire:\n\n',
  mode: 'fill'
};

const describePlan = (plan: AppPlan): string => {
  const price = plan.priceUsd === 0 ? 'free' : `$${plan.priceUsd} per month`;

  return `**${plan.name}** (${price}): ${plan.tagline.charAt(0).toLowerCase()}${plan.tagline.slice(1)}`;
};

const PLANS_REPLY = `Havu is your AI AppSec lead, billed monthly. ${APP_PLANS.map(describePlan).join(' ')}`;

const isMobileViewport = (): boolean => window.matchMedia(MOBILE_QUERY).matches;

const toIssueRef = (issue: RegisterIssue): AppIssueRef => ({
  key: issue.key,
  title: issue.cleanTitle,
  severity: issue.severity,
  summary: issue.summary,
  detail: issue.detail,
  impact: issue.impact,
  files: issue.files
});

const readPending = (): PendingAction | null => {
  try {
    return JSON.parse(window.localStorage.getItem(PENDING_STORAGE_KEY) ?? 'null') as PendingAction | null;
  } catch {
    return null;
  }
};

const writePending = (action: PendingAction | null): void => {
  try {
    if (action) {
      window.localStorage.setItem(PENDING_STORAGE_KEY, JSON.stringify(action));
    } else {
      window.localStorage.removeItem(PENDING_STORAGE_KEY);
    }
  } catch {
    // Without storage the request simply does not resume after the redirect.
  }
};

function AppPage() {
  const initialWorkspaceRoot = parseWorkspaceRoot(window.location.pathname);
  const account = useAppAccount();
  const setCurrentWorkspaceId = account.setCurrentWorkspaceId;
  const subscription = useAppPlan(account.isSignedIn);
  const workflowStore = useWorkflows(account.isSignedIn);
  const workspaceContext = useWorkspaceContext(
    account.workspaces.map((workspace) => workspace.id),
    account.isSignedIn
  );
  const refreshSubscription = subscription.refresh;
  const {
    chats,
    replyingChatIds,
    codexReplyChatIds,
    loadChat,
    addUserMessage,
    addAssistantMessage,
    updateWidget,
    withReply,
    replyWithText,
    cancelReply,
    runPentest,
    renameChat,
    deleteChat
  } = useAppChats(account.workspaces.map((entry) => entry.id), account.isSignedIn);
  const [view, setView] = useState<AppView>('chat');
  const [workspaceRoot, setWorkspaceRoot] = useState<AppWorkspaceRoot | null>(initialWorkspaceRoot);
  const [activeChatId, setActiveChatId] = useState<string | null>(
    initialWorkspaceRoot?.kind === 'chat' ? initialWorkspaceRoot.id : null
  );
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => !isMobileViewport());
  const [isCreatingWorkspace, setIsCreatingWorkspace] = useState(false);
  const [isLoginOpen, setIsLoginOpen] = useState(false);
  const [expandedWorkspaceIds, setExpandedWorkspaceIds] = useState<Set<string>>(() => new Set());
  const [issuesReloadKey, setIssuesReloadKey] = useState(0);
  const [resourcesReloadKey, setResourcesReloadKey] = useState(0);
  const [subscriptionCheckout, setSubscriptionCheckout] = useState<PendingSubscriptionCheckout | null>(null);
  const [billingError, setBillingError] = useState('');
  const [isBillingBusy, setIsBillingBusy] = useState(false);
  const [selectedIssueKey, setSelectedIssueKey] = useState<string | null>(null);
  const [issuesScanFilter, setIssuesScanFilter] = useState<string | null>(null);
  const [composerRefs, setComposerRefs] = useState<AppIssueRef[]>([]);
  const [workspaceResources, setWorkspaceResources] = useState<AppWorkspaceResourcePointer[]>([]);
  const [composerResourceIds, setComposerResourceIds] = useState<string[]>([]);
  const [composerZip, setComposerZip] = useState<File | null>(null);
  const [composerZipError, setComposerZipError] = useState('');
  const [isSendingMessage, setIsSendingMessage] = useState(false);
  const [repositoryChoices, setRepositoryChoices] = useState<AppRepositoryChoice[]>([]);
  const [repositoryChoicesState, setRepositoryChoicesState] = useState<LoadState>('idle');

  const workspaceId = account.currentWorkspaceId;
  const workspace = account.workspaces.find((entry) => entry.id === workspaceId);
  const activeChat = chats.find((chat) => chat.id === activeChatId);
  const { register, setStatus, reload: reloadIssues } = useWorkspaceIssues(
    account.isSignedIn ? workspaceId : '',
    issuesReloadKey
  );
  const [overviewReloadKey, setOverviewReloadKey] = useState(0);
  const overview = useSecurityOverview({
    accountId: account.isSignedIn ? account.accountUser?.id ?? '' : '',
    workspaces: account.workspaces,
    workspacesReady: account.workspacesState === 'ready',
    planId: subscription.plan.id,
    scansLeft: subscription.scansLeft,
    reloadKey: issuesReloadKey + overviewReloadKey
  });
  const selectedIssue = view === 'chat' || view === 'issues'
    ? register.issues.find((issue) => issue.key === selectedIssueKey)
    : undefined;
  const openIssueCount = register.issues.filter((issue) => issue.status === 'open').length;
  const showSuggestions = account.accountState === 'ready'
    && (!account.isSignedIn || (account.workspacesState === 'ready' && account.workspaces.length === 0));
  const composerResources = workspaceResources.filter((resource) => composerResourceIds.includes(resource.id));
  const hasActiveWorkflow = workflowStore.workflows.some((workflow) => workflow.isActive);
  const uploadedCodebases = useMemo(() => {
    return workspaceResources.filter((resource) => resource.kind === 'codebase' && resource.source === 'upload');
  }, [workspaceResources]);

  useEffect(() => {
    if (!account.isSignedIn || !workspaceId) {
      setWorkspaceResources([]);
      setComposerResourceIds([]);
      return undefined;
    }

    if (runIsDevGitHubAccountEnabled()) {
      setWorkspaceResources((current) => current.filter((resource) => resource.workspaceId === workspaceId));
      setComposerResourceIds([]);
      return undefined;
    }

    let cancelled = false;

    runRequestJson<{ resources?: AppWorkspaceResourcePointer[] }>(
      `/workspaces/${encodeURIComponent(workspaceId)}/resources?summary=true`
    )
      .then((response) => {
        if (!cancelled) {
          // The server enforces this boundary; retaining the check here also prevents a malformed
          // response from offering another workspace's pointer in the composer.
          const resources = (response.resources ?? []).filter((resource) => resource.workspaceId === workspaceId);

          setWorkspaceResources(resources);
          setComposerResourceIds((current) => current.filter((id) => resources.some((resource) => resource.id === id)));
        }
      })
      .catch((error: unknown) => {
        console.error('Failed to load workspace resources:', error);

        if (!cancelled) {
          setWorkspaceResources([]);
          setComposerResourceIds([]);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [account.isSignedIn, resourcesReloadKey, workspaceId]);

  const expandWorkspace = useCallback((id: string) => {
    setExpandedWorkspaceIds((current) => current.has(id) ? current : new Set(current).add(id));
  }, []);

  const navigateWorkspaceRoot = useCallback((root: AppWorkspaceRoot | null, replace = false) => {
    const path = root ? getWorkspaceRootPath(root) : getWorkspacePath();

    if (window.location.pathname !== path) {
      window.history[replace ? 'replaceState' : 'pushState']({}, document.title, path);
    }

    setWorkspaceRoot(root);
  }, []);

  // Browser navigation is intentionally small and dependency-free: only persisted roots need a
  // shareable URL. Other app views return to the workspace landing path.
  useEffect(() => {
    const restoreRoute = () => {
      const root = parseWorkspaceRoot(window.location.pathname);

      setWorkspaceRoot(root);
      setView('chat');
      setActiveChatId(root?.kind === 'chat' ? root.id : null);
      setSelectedIssueKey(null);
      setIssuesScanFilter(null);
      setComposerRefs([]);
      setComposerResourceIds([]);
      setComposerZip(null);
      setComposerZipError('');
    };

    window.addEventListener('popstate', restoreRoute);

    return () => window.removeEventListener('popstate', restoreRoute);
  }, []);

  // Once account chats arrive, a pasted/email chat URL selects both the chat and its workspace.
  useEffect(() => {
    if (workspaceRoot?.kind !== 'chat') {
      return;
    }

    const routedChat = chats.find((chat) => chat.id === workspaceRoot.id);

    if (routedChat) {
      setCurrentWorkspaceId(routedChat.workspaceId);
      expandWorkspace(routedChat.workspaceId);
      setView('chat');
      setActiveChatId(routedChat.id);
    }
  }, [chats, expandWorkspace, setCurrentWorkspaceId, workspaceRoot]);

  useEffect(() => {
    if (workspaceRoot?.kind !== 'chat' || !account.isSignedIn || runIsDevGitHubAccountEnabled()) {
      return;
    }

    void loadChat(workspaceRoot.id)
      .catch((error: unknown) => console.error('Failed to open workspace chat:', error));
  }, [account.isSignedIn, loadChat, workspaceRoot]);

  // Resource roots resolve through the same authenticated backend namespace. Until resources get
  // dedicated viewers, opening one prepares a new chat with that reusable resource attached.
  useEffect(() => {
    if (workspaceRoot?.kind !== 'resource' || !account.isSignedIn || runIsDevGitHubAccountEnabled()) {
      return undefined;
    }

    let cancelled = false;

    runRequestJson<{ resource: RoutedWorkspaceResource }>(getWorkspaceRootPath(workspaceRoot))
      .then(({ resource }) => {
        if (cancelled) {
          return;
        }

        const source = resource.data?.source === 'github' || resource.data?.source === 'upload'
          ? resource.data.source
          : resource.source;
        const pointer: AppWorkspaceResourcePointer = {
          id: resource.id,
          workspaceId: resource.workspaceId,
          kind: resource.kind,
          name: resource.name,
          state: resource.state,
          ...(source ? { source } : {})
        };

        setCurrentWorkspaceId(resource.workspaceId);
        expandWorkspace(resource.workspaceId);
        setWorkspaceResources((current) => [pointer, ...current.filter((entry) => entry.id !== pointer.id)]);
        setComposerResourceIds(source === 'upload' ? [] : [resource.id]);
        setView('chat');
        setActiveChatId(null);
      })
      .catch((error: unknown) => console.error('Failed to open workspace resource:', error));

    return () => {
      cancelled = true;
    };
  }, [account.isSignedIn, expandWorkspace, setCurrentWorkspaceId, workspaceRoot]);

  useEffect(() => {
    if (workspaceId) {
      expandWorkspace(workspaceId);
    }
  }, [expandWorkspace, workspaceId]);

  useEffect(() => {
    if (account.isSignedIn) {
      setIsLoginOpen(false);
    }
  }, [account.isSignedIn]);

  useEffect(() => {
    if (workspaceRoot && account.accountState === 'ready' && !account.isSignedIn) {
      setIsLoginOpen(true);
    }
  }, [account.accountState, account.isSignedIn, workspaceRoot]);

  // Repository names for the sidebar's scope labels and the Scope page's picker.
  useEffect(() => {
    if (account.githubState !== 'connected') {
      setRepositoryChoices([]);
      setRepositoryChoicesState('idle');
      return undefined;
    }

    let cancelled = false;

    setRepositoryChoicesState('loading');
    loadRepositoryChoices(account.githubSessionId, account.githubInstallations)
      .then((choices) => {
        if (!cancelled) {
          setRepositoryChoices(choices);
          setRepositoryChoicesState('ready');
        }
      })
      .catch((error: unknown) => {
        console.error('Failed to load repositories:', error);

        if (!cancelled) {
          setRepositoryChoicesState('error');
        }
      });

    return () => {
      cancelled = true;
    };
  }, [account.githubInstallations, account.githubSessionId, account.githubState]);

  const getScopeLabel = useCallback((entry: Workspace): string | undefined => {
    const scope = entry.scope;

    return repositoryChoices.find((choice) => {
      return choice.installationId === scope?.installationId && choice.repository.id === scope.repositoryId;
    })?.repository.repo;
  }, [repositoryChoices]);

  // Lock the page to the viewport; only the thread, lists and sidebar scroll.
  useEffect(() => {
    document.documentElement.classList.add('app-page-root');

    return () => document.documentElement.classList.remove('app-page-root');
  }, []);

  useEffect(() => {
    const mediaQuery = window.matchMedia(MOBILE_QUERY);
    const syncSidebar = () => setIsSidebarOpen(!mediaQuery.matches);

    mediaQuery.addEventListener('change', syncSidebar);

    return () => mediaQuery.removeEventListener('change', syncSidebar);
  }, []);

  useEffect(() => {
    if (!selectedIssue) {
      return undefined;
    }

    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && !event.defaultPrevented) {
        setSelectedIssueKey(null);
      }
    };

    window.addEventListener('keydown', closeOnEscape);

    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [selectedIssue]);

  const closeSidebarOnMobile = useCallback(() => {
    if (isMobileViewport()) {
      setIsSidebarOpen(false);
    }
  }, []);

  const openView = useCallback((nextView: AppView, nextWorkspaceId?: string, chatId: string | null = null) => {
    if (nextWorkspaceId) {
      account.setCurrentWorkspaceId(nextWorkspaceId);
      expandWorkspace(nextWorkspaceId);
    }

    setView(nextView);
    setActiveChatId(chatId);
    setSelectedIssueKey(null);
    setIssuesScanFilter(null);
    setComposerRefs([]);
    setComposerResourceIds([]);
    setComposerZip(null);
    setComposerZipError('');
    navigateWorkspaceRoot(nextView === 'chat' && chatId ? { kind: 'chat', id: chatId } : null);
    closeSidebarOnMobile();
  }, [account, closeSidebarOnMobile, expandWorkspace, navigateWorkspaceRoot]);

  const openResource = useCallback((resourceId: string) => {
    const resource = workspaceResources.find((entry) => entry.id === resourceId);

    if (resource) {
      account.setCurrentWorkspaceId(resource.workspaceId);
      expandWorkspace(resource.workspaceId);
      setComposerResourceIds(resource.source === 'upload' ? [] : [resource.id]);
    }

    setView('chat');
    setActiveChatId(null);
    setSelectedIssueKey(null);
    setComposerRefs([]);
    setComposerZip(null);
    setComposerZipError('');
    navigateWorkspaceRoot({ kind: 'resource', id: resourceId });
    closeSidebarOnMobile();
  }, [account, closeSidebarOnMobile, expandWorkspace, navigateWorkspaceRoot, workspaceResources]);

  const openLogin = useCallback((pending?: PendingAction) => {
    if (pending) {
      writePending(pending);
    }

    setIsLoginOpen(true);
  }, []);

  const closeLogin = useCallback(() => {
    const pending = readPending();

    if (pending && 'uploadKey' in pending) {
      void deletePendingZip(pending.uploadKey).catch(() => undefined);
    }

    writePending(null);
    setIsLoginOpen(false);
  }, []);

  const startCreateWorkspace = useCallback(() => {
    if (!account.isSignedIn) {
      openLogin();
      return;
    }

    if (account.workspaces.length >= subscription.plan.workspaceLimit) {
      openView('pricing');
      return;
    }

    setIsSidebarOpen(true);
    setIsCreatingWorkspace(true);
  }, [account.isSignedIn, account.workspaces.length, openLogin, openView, subscription.plan.workspaceLimit]);

  const createWorkspace = useCallback(async (name: string): Promise<boolean> => {
    setIsCreatingWorkspace(false);
    const created = await account.createWorkspace(name);

    if (created) {
      openView('chat', created.id);
    }

    return Boolean(created);
  }, [account, openView]);

  const connectGitHub = useCallback(() => {
    if (account.githubConfig?.connectUrl) {
      account.connectGitHub();
    } else {
      openLogin();
    }
  }, [account, openLogin]);

  const toggleWorkspace = useCallback((id: string) => {
    setExpandedWorkspaceIds((current) => {
      const next = new Set(current);

      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }

      return next;
    });
  }, []);

  /* ── Scans from chat ──────────────────────────────────────────────────── */

  /** Answers a pricing request with the plans, and a pentest request with GitHub (or a ZIP upload), then a repository, then the run card. */
  const runScanFlow = useCallback(async (chatId: string, intent: AppScanIntent, chatWorkspaceId: string) => {
    if (intent === 'pricing') {
      await addAssistantMessage(chatId, PLANS_REPLY, { type: 'plans' });
      return;
    }

    const scope = account.getWorkspace(chatWorkspaceId)?.scope;

    if (scope && account.githubState === 'connected') {
      const choices = await loadRepositoryChoices(account.githubSessionId, account.githubInstallations);
      const choice = choices.find((entry) => {
        return entry.installationId === scope.installationId && entry.repository.id === scope.repositoryId;
      });

      if (choice) {
        await addAssistantMessage(chatId, '', { type: 'pricing', intent, target: toScanTarget(choice, scope) });
        return;
      }
    }

    const uploadedCodebase = findReusableUploadedCodebase(workspaceResources, chatWorkspaceId);

    if (uploadedCodebase) {
      const details = await loadUploadedCodebaseDetails(uploadedCodebase);

      await addAssistantMessage(chatId, '', {
        type: 'pricing',
        intent,
        target: toUploadedScanTarget(details)
      });
      return;
    }

    if (account.githubState !== 'connected') {
      await addAssistantMessage(
        chatId,
        scope
          ? `Reconnect GitHub so Havu can reach ${scope.owner && scope.repo ? `${scope.owner}/${scope.repo}` : 'this workspace\'s repository'}.`
          : account.githubState === 'needs-install'
            ? 'Give Havu access to a repository to continue, or upload a ZIP of your codebase if you would rather not share repository access.'
            : 'Connect GitHub to choose a repository, or upload a ZIP of your codebase if you would rather not share repository access.',
        { type: 'connect-github', intent }
      );
      return;
    }

    await addAssistantMessage(
      chatId,
      scope
        ? 'The repository saved for this workspace is no longer accessible. Which repository should Havu test?'
        : 'Which repository? You can also upload a ZIP of your codebase from the + menu instead.',
      { type: 'repo-picker', intent }
    );
  }, [account, addAssistantMessage, workspaceResources]);

  const handleScanLaunched = useCallback(async (chatId: string, messageId: string) => {
    updateWidget(chatId, messageId, { launched: true });
    setIssuesReloadKey((key) => key + 1);
    setResourcesReloadKey((key) => key + 1);
    void refreshSubscription().catch((error: unknown) => {
      console.error('The pentest started, but the subscription state could not be refreshed:', error);
    });
  }, [refreshSubscription, updateWidget]);

  /** Starts a pentest with the active user workflow, or the backend default. */
  const launchScan = useCallback(async (
    chatId: string,
    messageId: string,
    target: AppPentestTarget,
    chatWorkspaceId: string
  ) => {
    if (isUploadedScanTarget(target)) {
      await runPentest(chatId, target.codebaseResourceId);
      await handleScanLaunched(chatId, messageId);
      return;
    }

    const currentScope = account.getWorkspace(chatWorkspaceId)?.scope;
    const updatedWorkspace = await account.saveWorkspaceScope(chatWorkspaceId, {
      installationId: target.installationId,
      repositoryId: target.repositoryId,
      owner: target.owner,
      repo: target.repo,
      branch: target.branch,
      scopePaths: target.scopePaths,
      scanType: target.scanType,
      ...(target.assumptionsFilePath ? { assumptionsFilePath: target.assumptionsFilePath } : {}),
      ...(currentScope?.codebaseResourceId ? { codebaseResourceId: currentScope.codebaseResourceId } : {})
    });
    const codebaseResourceId = updatedWorkspace.scope?.codebaseResourceId;

    if (!codebaseResourceId) {
      throw new Error('The workspace codebase could not be prepared.');
    }

    await runPentest(chatId, codebaseResourceId, account.githubSessionId);
    await handleScanLaunched(chatId, messageId);
  }, [account, handleScanLaunched, runPentest]);

  /**
   * ZIP results are delivered by email. A GitHub login only carries a public email, so ask for one
   * in the chat when the account has none; the reply resumes the pentest.
   */
  const runScan = useCallback(async (
    chatId: string,
    messageId: string,
    target: AppPentestTarget,
    chatWorkspaceId: string
  ) => {
    if (isUploadedScanTarget(target) && !account.accountUser?.email) {
      await addAssistantMessage(
        chatId,
        'Where should I send the results? Your GitHub account does not share an email address with Havu, '
          + 'so reply with the address you would like the assessment report sent to.',
        { type: 'email-request', target, pricingMessageId: messageId }
      );
      return;
    }

    await launchScan(chatId, messageId, target, chatWorkspaceId);
  }, [account.accountUser?.email, addAssistantMessage, launchScan]);

  /** Saves the email from a reply to an email request, then starts the pentest it was holding. */
  const provideEmail = useCallback(async (
    chatId: string,
    chatWorkspaceId: string,
    request: { messageId: string; widget: AppEmailRequestWidget },
    content: string
  ) => {
    const { target, pricingMessageId } = request.widget;
    const email = findEmailAddress(content);

    if (!email) {
      await addAssistantMessage(
        chatId,
        'That does not look like an email address. Reply with one like `name@example.com` and I will start the assessment.',
        { type: 'email-request', target, pricingMessageId }
      );
      return;
    }

    try {
      await account.updateEmail(email);
    } catch (error) {
      const reason = error instanceof Error && error.message ? ` ${error.message}` : '';

      await addAssistantMessage(
        chatId,
        `I could not save that email address.${reason} Reply with an address to try again.`,
        { type: 'email-request', target, pricingMessageId }
      );
      return;
    }

    updateWidget(chatId, request.messageId, { email });
    await addAssistantMessage(
      chatId,
      `Thanks. Results will be sent to **${email}**. You can change this anytime in Settings.`
    );

    try {
      await launchScan(chatId, pricingMessageId, target, chatWorkspaceId);
    } catch (error) {
      if (!(error instanceof AppChatReportedError)) {
        await addAssistantMessage(
          chatId,
          error instanceof Error && error.message ? error.message : 'The assessment could not be started.'
        );
      }
    }
  }, [account, addAssistantMessage, launchScan, updateWidget]);

  const send = useCallback((content: string, options: SendOptions): boolean => {
    if (isSendingMessage) {
      return false;
    }

    if (!account.isSignedIn) {
      openLogin({ prompt: content });
      return false;
    }

    setIsSendingMessage(true);
    const { detectIntent = true, issueRefs, resourceIds, title } = options;
    // A reply with an address answers a pending email request. Anything else is a normal message,
    // so the user is never stuck answering the request; the Run button asks again.
    const emailRequest = content.includes('@')
      ? getPendingEmailRequest(chats.find((chat) => chat.id === options.chatId))
      : undefined;

    const proceed = async (target: Workspace) => {
      try {
        const chatId = await addUserMessage(
          { chatId: target.id === workspaceId ? options.chatId : null, workspaceId: target.id },
          content,
          { issueRefs, resourceIds, title }
        );
        const intent = detectIntent ? detectScanIntent(content) : null;

        setActiveChatId(chatId);
        navigateWorkspaceRoot({ kind: 'chat', id: chatId });
        expandWorkspace(target.id);

        if (emailRequest && chatId === options.chatId) {
          await withReply(chatId, () => provideEmail(chatId, target.id, emailRequest, content));
        } else if (intent) {
          await withReply(chatId, () => runScanFlow(chatId, intent, target.id));
        } else {
          await replyWithText(chatId);
        }
      } catch (error) {
        console.error('Failed to send chat message:', error);
      } finally {
        setIsSendingMessage(false);
      }
    };

    const targetWorkspace = options.workspaceId ? account.getWorkspace(options.workspaceId) : workspace;

    if (targetWorkspace) {
      void proceed(targetWorkspace);
    } else {
      void account.ensureWorkspace()
        .then((created) => {
          if (created) {
            void proceed(created);
          } else {
            setIsSendingMessage(false);
          }
        })
        .catch((error: unknown) => {
          console.error('Failed to create the first workspace:', error);
          setIsSendingMessage(false);
        });
    }

    return true;
  }, [
    account,
    addUserMessage,
    chats,
    expandWorkspace,
    isSendingMessage,
    openLogin,
    navigateWorkspaceRoot,
    provideEmail,
    replyWithText,
    runScanFlow,
    withReply,
    workspace,
    workspaceId
  ]);

  /** Uploads a selected ZIP into the workspace and creates a pentest-only pricing card. */
  const sendUploadedZip = useCallback(async (
    file: File,
    content: string
  ): Promise<boolean> => {
    if (isSendingMessage) {
      return false;
    }

    const validationError = getCodebaseZipValidationError(file);

    if (validationError) {
      setComposerZipError(validationError);
      return false;
    }

    if (!account.isSignedIn) {
      setIsSendingMessage(true);
      setComposerZipError('');

      try {
        const uploadKey = await storePendingZip(file);

        openLogin({ uploadKey, prompt: content });
      } catch (error) {
        console.error('Failed to retain the ZIP for login:', error);
        setComposerZipError('The ZIP could not be retained for login. Please try again.');
      } finally {
        setIsSendingMessage(false);
      }

      return false;
    }

    setIsSendingMessage(true);
    setComposerZipError('');

    try {
      const targetWorkspace = workspace ?? await account.ensureWorkspace();

      if (!targetWorkspace) {
        throw new Error('A workspace could not be created.');
      }

      const resource = await uploadCodebaseZip(targetWorkspace.id, file, content);
      const resourcePointer: AppWorkspaceResourcePointer = {
        id: resource.id,
        workspaceId: resource.workspaceId,
        kind: resource.kind,
        name: resource.name,
        state: resource.state,
        source: 'upload'
      };
      const messageContent = content || `Run a security assessment of ${file.name}.`;
      const chatId = await addUserMessage(
        {
          chatId: targetWorkspace.id === workspaceId ? activeChatId : null,
          workspaceId: targetWorkspace.id
        },
        messageContent,
        {
          resourceIds: [resource.id],
          title: `Assess ${file.name}`
        }
      );

      setWorkspaceResources((current) => [
        resourcePointer,
        ...current.filter((entry) => entry.id !== resource.id)
      ]);
      setResourcesReloadKey((key) => key + 1);
      setView('chat');
      setActiveChatId(chatId);
      navigateWorkspaceRoot({ kind: 'chat', id: chatId });
      expandWorkspace(targetWorkspace.id);
      await addAssistantMessage(chatId, '', {
        type: 'pricing',
        intent: 'pentest',
        target: {
          source: 'upload',
          fileName: file.name,
          sizeBytes: file.size,
          codebaseResourceId: resource.id
        }
      });

      setComposerZip(null);
      setComposerZipError('');

      return true;
    } catch (error) {
      console.error('Failed to upload the codebase ZIP:', error);
      setComposerZip(file);
      setComposerZipError(error instanceof Error && error.message
        ? error.message
        : 'The ZIP could not be uploaded.');
      return false;
    } finally {
      setIsSendingMessage(false);
    }
  }, [
    account,
    activeChatId,
    addAssistantMessage,
    addUserMessage,
    expandWorkspace,
    isSendingMessage,
    navigateWorkspaceRoot,
    openLogin,
    workspace,
    workspaceId
  ]);

  /** Pentests a ZIP picked outside the composer, such as on the connect GitHub card or the Scope page. */
  const pentestZip = useCallback((file: File) => {
    const validationError = getCodebaseZipValidationError(file);

    if (validationError) {
      setComposerZipError(validationError);
      return;
    }

    setComposerZip(file);
    void sendUploadedZip(file, '');
  }, [sendUploadedZip]);

  const pickRepository = useCallback(async (
    chatId: string,
    messageId: string,
    intent: AppScanIntent,
    chatWorkspaceId: string,
    choice: AppRepositoryChoice
  ) => {
    const scope: WorkspaceScope = {
      installationId: choice.installationId,
      repositoryId: choice.repository.id,
      owner: choice.repository.owner,
      repo: choice.repository.repo,
      branch: choice.repository.defaultBranch || 'main',
      scopePaths: [],
      scanType: 'smart-contract'
    };

    const updatedWorkspace = await account.saveWorkspaceScope(chatWorkspaceId, scope);
    setResourcesReloadKey((key) => key + 1);
    updateWidget(chatId, messageId, { chosen: choice.repository.fullName });
    await addAssistantMessage(chatId, '', {
      type: 'pricing',
      intent,
      target: toScanTarget(choice, updatedWorkspace.scope ?? scope)
    });
  }, [account, addAssistantMessage, updateWidget]);

  const selectSubscriptionPlan = useCallback(async (planId: AppPlanId) => {
    setBillingError('');

    if (!account.isSignedIn) {
      openLogin();
      return;
    }

    if (planId === 'none') return;

    setSubscriptionCheckout({ plan: getAppPlan(planId), isSubmitting: false, error: '' });
  }, [account.isSignedIn, openLogin]);

  const cancelSubscription = useCallback(async () => {
    setBillingError('');

    if (!account.isSignedIn || !subscription.subscription) return;

    if (!window.confirm('Cancel your subscription at the end of the current billing period?')) {
      return;
    }

    setIsBillingBusy(true);
    try {
      subscription.applyState(await cancelCurrentSubscription());
    } catch (error: unknown) {
      setBillingError(error instanceof Error ? error.message : 'Unable to cancel the subscription.');
    } finally {
      setIsBillingBusy(false);
    }
  }, [account.isSignedIn, subscription]);

  const submitSubscription = useCallback(async (submission: SubscriptionPaymentSubmission) => {
    if (!subscriptionCheckout || subscriptionCheckout.plan.id === 'none') return;

    setSubscriptionCheckout({ ...subscriptionCheckout, isSubmitting: true, error: '' });

    try {
      subscription.applyState(await checkoutSubscription(subscriptionCheckout.plan.id, submission));
      setSubscriptionCheckout(null);
    } catch (error: unknown) {
      setSubscriptionCheckout({
        ...subscriptionCheckout,
        isSubmitting: false,
        error: error instanceof Error && error.message ? error.message : 'Unable to activate the subscription.'
      });
    }
  }, [subscription, subscriptionCheckout]);

  /* ── Issues ───────────────────────────────────────────────────────────── */

  const viewScanIssues = useCallback((chatWorkspaceId: string, runId: string) => {
    openView('issues', chatWorkspaceId);

    // Filter to the scan only once the register holds it; otherwise the list would look empty.
    if (register.workspaceId === chatWorkspaceId && register.reportScans.some((scan) => scan.id === runId)) {
      setIssuesScanFilter(runId);
    }
  }, [openView, register.reportScans, register.workspaceId]);

  /** Starts a new chat with the issues attached to the composer, ready for a question. */
  const askAboutIssues = useCallback((issues: RegisterIssue[]) => {
    openView('chat', workspaceId);
    setComposerRefs(issues.map(toIssueRef));
  }, [openView, workspaceId]);

  /** Starts a fix conversation tied to the issue, in the application that reported it. */
  const fixIssue = useCallback((issue: RegisterIssue, issueWorkspaceId = workspaceId) => {
    openView('chat', issueWorkspaceId);
    send('Fix this issue.', {
      chatId: null,
      workspaceId: issueWorkspaceId,
      detectIntent: false,
      issueRefs: [toIssueRef(issue)],
      title: `Fix: ${issue.cleanTitle}`
    });
  }, [openView, send, workspaceId]);

  /** Carries out one of Havu's next steps; assessments still wait for the user to press Run. */
  const runOverviewAction = useCallback((action: OverviewAction) => {
    const target = action.workspaceId;

    if (action.kind === 'allowance' || !target) {
      openView('pricing');
    } else if (action.kind === 'setup') {
      openView('scope', target);
    } else if (action.kind === 'triage') {
      openView('issues', target);
    } else if (action.issue) {
      fixIssue(action.issue, target);
    } else {
      openView('chat', target);
      send(ASSESSMENT_PROMPT, { chatId: null, workspaceId: target, title: action.title });
    }
  }, [fixIssue, openView, send]);

  const renderWidget = useCallback((chat: AppChat | undefined) => (message: AppChatMessage) => {
    const widget = message.widget;

    if (!chat || !widget) {
      return null;
    }

    switch (widget.type) {
      case 'connect-github': {
        const pending: PendingAction = { chatId: chat.id, intent: widget.intent };

        return (
          <AppConnectGitHubWidget
            githubState={account.githubState}
            onConnect={() => {
              writePending(pending);
              connectGitHub();
            }}
            onManage={() => {
              writePending(pending);
              account.manageGitHubAccess();
            }}
            onUploadZip={account.getWorkspace(chat.workspaceId)?.scope ? undefined : pentestZip}
          />
        );
      }
      case 'repo-picker': {
        const { intent } = widget;

        return (
          <AppRepoPickerWidget
            sessionId={account.githubSessionId}
            installations={account.githubInstallations}
            chosen={widget.chosen}
            onPick={(choice) => pickRepository(chat.id, message.id, intent, chat.workspaceId, choice)}
          />
        );
      }
      case 'pricing': {
        return (
          <AppRunPentestWidget
            target={widget.target}
            plan={subscription.plan}
            scansLeft={subscription.scansLeft}
            resetsAt={subscription.resetsAt}
            isLaunched={Boolean(widget.launched)}
            hasActiveWorkflow={hasActiveWorkflow}
            onRun={() => runScan(chat.id, message.id, widget.target, chat.workspaceId)}
            onEditScope={isUploadedScanTarget(widget.target)
              ? undefined
              : () => openView('scope', chat.workspaceId)}
            onEditWorkflow={() => openView('workflows')}
            onViewPlans={() => openView('pricing')}
          />
        );
      }
      case 'plans':
        return <AppPlansWidget onViewPlans={() => openView('pricing')} />;
      case 'email-request':
        // Answered in the composer, like any other chat reply.
        return null;
      case 'pentest': {
        const { runId } = widget;
        const isInRegister = register.workspaceId === chat.workspaceId
          && register.reportScans.some((scan) => scan.id === runId);

        return (
          <AppPentestProgressWidget
            runId={runId}
            isAutoWorkflow={message.isAutoWorkflow}
            newCount={isInRegister
              ? register.issues.filter((issue) => issue.firstSeen.id === runId).length
              : undefined}
            onFinished={() => setIssuesReloadKey((key) => key + 1)}
            onViewIssues={() => viewScanIssues(chat.workspaceId, runId)}
          />
        );
      }
      default:
        return null;
    }
  }, [
    account,
    connectGitHub,
    hasActiveWorkflow,
    openView,
    pentestZip,
    pickRepository,
    register,
    runScan,
    subscription.plan,
    subscription.resetsAt,
    subscription.scansLeft,
    viewScanIssues
  ]);

  const suggestions = useMemo<AppChatSuggestion[]>(() => [
    ASSESS_SUGGESTION,
    {
      label: 'Compare plans',
      icon: <IconTag aria-hidden={true} />,
      prompt: '',
      mode: 'send',
      onSelect: () => openView('pricing')
    },
    QUESTIONNAIRE_SUGGESTION,
    REPORT_SUGGESTION
  ], [openView]);

  // Signed-in users with applications see Havu's top next steps instead of starter prompts.
  const hasBriefing = account.isSignedIn && account.workspaces.length > 0 && overview.state === 'ready';
  const briefingActions = useMemo(() => {
    return hasBriefing ? overview.actions.slice(0, BRIEFING_ACTIONS) : [];
  }, [hasBriefing, overview.actions]);
  const briefingSuggestions = useMemo<AppChatSuggestion[]>(() => briefingActions.map((action) => ({
    label: action.title,
    icon: ACTION_ICONS[action.kind],
    prompt: '',
    mode: 'send',
    onSelect: () => runOverviewAction(action)
  })), [briefingActions, runOverviewAction]);
  const chatHero = composerZip
    ? undefined
    : briefingActions.length > 0 ? (
      <p className="app-briefing">
        <span>
          {overview.actions.length === 1
            ? '1 thing needs your attention across your applications.'
            : `${overview.actions.length} things need your attention across your applications.`}
        </span>
      </p>
    ) : showSuggestions ? <AppLineup onOpen={() => openView('tools')} /> : undefined;

  // Continue what the user asked for before a login or GitHub redirect.
  const latestRef = useRef({ send, sendUploadedZip, runScanFlow, withReply, chats });

  latestRef.current = { send, sendUploadedZip, runScanFlow, withReply, chats };

  useEffect(() => {
    if (!account.isSignedIn || account.workspacesState !== 'ready') {
      return;
    }

    const pending = readPending();

    if (!pending) {
      return;
    }

    if ('uploadKey' in pending) {
      writePending(null);
      setView('chat');
      void loadPendingZip(pending.uploadKey)
        .then(async (file) => {
          if (!file) {
            setComposerZipError('The selected ZIP is no longer available. Please attach it again.');
            return;
          }

          setComposerZip(file);
          await deletePendingZip(pending.uploadKey).catch(() => undefined);
          await latestRef.current.sendUploadedZip(file, pending.prompt);
        })
        .catch((error: unknown) => {
          console.error('Failed to restore the ZIP after login:', error);
          setComposerZipError('The selected ZIP could not be restored. Please attach it again.');
        });
      return;
    }

    if ('prompt' in pending) {
      writePending(null);
      setView('chat');
      latestRef.current.send(pending.prompt, { chatId: null });
      return;
    }

    if (account.githubState !== 'connected') {
      return;
    }

    writePending(null);
    const chat = latestRef.current.chats.find((entry) => entry.id === pending.chatId);

    if (chat) {
      void latestRef.current.withReply(chat.id, () => latestRef.current.runScanFlow(chat.id, pending.intent, chat.workspaceId));
    }
  }, [account.githubState, account.isSignedIn, account.workspacesState]);

  const handleDeleteChat = useCallback((chatId: string) => {
    deleteChat(chatId);

    if (chatId === activeChatId) {
      setActiveChatId(null);
      navigateWorkspaceRoot(null, true);
    }
  }, [activeChatId, deleteChat, navigateWorkspaceRoot]);

  const clearWorkspaceContext = workspaceContext.clearWorkspace;

  const handleDeleteWorkspace = useCallback((id: string) => {
    const target = account.getWorkspace(id);

    if (!target || !window.confirm(`Delete ${target.name} and its chats and resources?`)) {
      return;
    }

    void account.deleteWorkspace(id).then((deleted) => {
      if (deleted) {
        clearWorkspaceContext(id);
      }

      if (deleted && id === workspaceId) {
        setView('chat');
        setActiveChatId(null);
        setSelectedIssueKey(null);
        navigateWorkspaceRoot(null, true);
      }
    });
  }, [account, clearWorkspaceContext, navigateWorkspaceRoot, workspaceId]);

  const handleLogout = useCallback(async (): Promise<boolean> => {
    if (!await account.logout()) {
      return false;
    }

    setView('chat');
    setActiveChatId(null);
    setSelectedIssueKey(null);
    navigateWorkspaceRoot(null, true);

    return true;
  }, [account, navigateWorkspaceRoot]);

  // Settings belong to a signed-in account; leave the view once the session ends.
  useEffect(() => {
    if (view === 'settings' && account.accountState === 'ready' && !account.isSignedIn) {
      setView('chat');
    }
  }, [account.accountState, account.isSignedIn, view]);

  const title = view === 'tools'
    ? 'Tools'
    : view === 'workflows'
      ? 'Workflows'
      : view === 'pricing'
        ? 'Plans'
        : view === 'settings'
          ? 'Settings'
          : view === 'scope'
            ? 'Scope'
            : view === 'context'
              ? 'Context'
              : view === 'issues' ? 'Issues' : activeChat?.title ?? 'New chat';
  const showWorkspaceCrumb = view !== 'tools'
    && view !== 'workflows'
    && view !== 'pricing'
    && view !== 'settings'
    && Boolean(workspace);

  return (
    <div className={`app-page${isSidebarOpen ? ' sidebar-open' : ''}${selectedIssue ? ' panel-open' : ''}`}>
      <AppSidebar
        view={view}
        activeChatId={activeChatId}
        activeWorkspaceId={workspaceId}
        workspaces={account.workspaces}
        workspacesState={account.workspacesState}
        chats={chats}
        expandedWorkspaceIds={expandedWorkspaceIds}
        openIssueCount={register.state === 'ready' ? openIssueCount : undefined}
        getScopeLabel={getScopeLabel}
        getContextCount={(id) => workspaceContext.getEntries(id).length}
        isCreatingWorkspace={isCreatingWorkspace}
        githubState={account.githubState}
        accountUser={account.accountUser}
        isSignedIn={account.isSignedIn}
        githubLogin={account.githubLogin}
        planName={subscription.plan.name}
        onToggleSidebar={() => setIsSidebarOpen(false)}
        onNewChat={() => openView('chat')}
        onStartCreateWorkspace={startCreateWorkspace}
        onCancelCreateWorkspace={() => setIsCreatingWorkspace(false)}
        onCreateWorkspace={createWorkspace}
        onRenameWorkspace={account.renameWorkspace}
        onDeleteWorkspace={handleDeleteWorkspace}
        onConnectGitHub={connectGitHub}
        onOpenTools={() => openView('tools')}
        onOpenWorkflows={() => openView('workflows')}
        onOpenPlans={() => openView('pricing')}
        onOpenSettings={() => openView('settings')}
        onToggleWorkspace={toggleWorkspace}
        onNewChatInWorkspace={(id) => openView('chat', id)}
        onSelectScope={(id) => openView('scope', id)}
        onSelectContext={(id) => openView('context', id)}
        onSelectIssues={(id) => openView('issues', id)}
        onSelectChat={(chat: AppChat) => openView('chat', chat.workspaceId, chat.id)}
        onRenameChat={renameChat}
        onDeleteChat={handleDeleteChat}
        onLogin={() => openLogin()}
        onLogout={() => void handleLogout()}
      />

      <button
        type="button"
        className="app-scrim"
        aria-label="Close sidebar"
        tabIndex={-1}
        onClick={() => setIsSidebarOpen(false)}
      />

      <main className="app-main">
        <header className="app-main-head">
          <div className="app-main-head-actions">
            <button
              type="button"
              className="app-icon-button"
              aria-label="Open sidebar"
              title="Open sidebar"
              onClick={() => setIsSidebarOpen(true)}
            >
              <IconSidebar aria-hidden={true} />
            </button>
            <button
              type="button"
              className="app-icon-button"
              aria-label="New chat"
              title="New chat"
              onClick={() => openView('chat')}
            >
              <IconCompose aria-hidden={true} />
            </button>
          </div>
          <div className="app-crumbs">
            {showWorkspaceCrumb && (
              <>
                <span className="app-crumb-muted">{workspace?.name}</span>
                <IconChevronRight aria-hidden={true} />
              </>
            )}
            <span className="app-crumb-current">{title}</span>
          </div>
        </header>

        {view === 'tools' && <AppToolsView />}

        {view === 'workflows' && (
          <AppWorkflowsView
            plan={subscription.plan}
            isSignedIn={account.isSignedIn}
            workflowStore={workflowStore}
          />
        )}

        {view === 'pricing' && (
          <AppPricingView
            plans={subscription.plans}
            currentPlanId={subscription.plan.id}
            isSignedIn={account.isSignedIn}
            onSelectPlan={(planId) => void selectSubscriptionPlan(planId)}
            onCancelSubscription={() => void cancelSubscription()}
            onLogin={() => openLogin()}
            isBusy={isBillingBusy || subscription.isLoading || Boolean(subscriptionCheckout?.isSubmitting)}
            error={billingError}
            hasSubscription={Boolean(subscription.subscription && subscription.subscription.status !== 'canceled')}
            cancelAtPeriodEnd={subscription.subscription?.cancelAtPeriodEnd}
            currentPeriodEnd={subscription.subscription?.currentPeriodEnd}
          />
        )}

        {view === 'settings' && account.isSignedIn && (
          <AppSettingsView
            accountUser={account.accountUser}
            githubLogin={account.githubLogin}
            githubState={account.githubState}
            installationCount={account.githubInstallations.length}
            canManageGitHub={Boolean(account.githubConfig?.installUrl)}
            plan={subscription.plan}
            scansLeft={subscription.scansLeft}
            onConnectGitHub={connectGitHub}
            onManageGitHub={account.manageGitHubAccess}
            onOpenPlans={() => openView('pricing')}
            onSaveEmail={account.updateEmail}
            onLogout={handleLogout}
          />
        )}

        {view === 'scope' && workspace && (
          <AppScopeView
            workspace={workspace}
            githubState={account.githubState}
            sessionId={account.githubSessionId}
            choices={repositoryChoices}
            choicesState={repositoryChoicesState}
            uploadedCodebases={uploadedCodebases}
            onConnectGitHub={connectGitHub}
            onManageGitHub={account.manageGitHubAccess}
            onUploadZip={(file) => {
              openView('chat', workspace.id);
              pentestZip(file);
            }}
            onSave={async (scope) => {
              await account.saveWorkspaceScope(workspace.id, scope);
              setResourcesReloadKey((key) => key + 1);
            }}
          />
        )}

        {view === 'context' && workspace && !workspaceContext.isReady(workspace.id) && (
          <div className="app-scroll">
            <div className="app-context">
              <p className="app-context-note">Loading workspace context…</p>
            </div>
          </div>
        )}

        {view === 'context' && workspace && workspaceContext.isReady(workspace.id) && (
          <AppContextView
            key={workspace.id}
            workspace={workspace}
            entries={workspaceContext.getEntries(workspace.id)}
            saveError={workspaceContext.getError(workspace.id)}
            onAdd={(section, text) => workspaceContext.addEntry(workspace.id, section, text)}
            onUpdate={(entryId, change) => workspaceContext.updateEntry(workspace.id, entryId, change)}
            onRemove={(entryId) => workspaceContext.removeEntry(workspace.id, entryId)}
            onAddFiles={(files) => workspaceContext.addFiles(workspace.id, files)}
            onOpenFile={(entry) => workspaceContext.openFile(workspace.id, entry)}
          />
        )}

        {view === 'chat' && (
          <AppChatView
            threadKey={activeChat?.id ?? (workspaceRoot?.kind === 'resource'
              ? `resource-${workspaceRoot.id}`
              : `new-${workspaceId}`)}
            messages={activeChat?.messages ?? []}
            isReplying={isSendingMessage || Boolean(activeChat && replyingChatIds.has(activeChat.id))}
            isCodexReplying={Boolean(activeChat && codexReplyChatIds.has(activeChat.id))}
            placeholder={composerZip
              ? 'Add assessment context (optional)'
              : composerRefs.length > 0
                ? 'Ask about the attached issues'
                : getPendingEmailRequest(activeChat)
                  ? 'Reply with your email address'
                  : workspace ? `Message ${workspace.name}` : 'Message Havu'}
            greeting="What should we secure?"
            hero={chatHero}
            suggestions={composerZip
              ? undefined
              : briefingSuggestions.length > 0 ? briefingSuggestions : showSuggestions ? suggestions : undefined}
            renderWidget={renderWidget(activeChat)}
            attachments={composerRefs}
            resources={workspaceResources}
            resourceAttachments={composerResources}
            zipAttachment={composerZip ? { name: composerZip.name, size: composerZip.size } : undefined}
            zipError={composerZipError}
            onRemoveAttachment={(key) => setComposerRefs((current) => current.filter((entry) => entry.key !== key))}
            onAttachResource={composerZip ? undefined : (resourceId) => {
              setComposerResourceIds((current) => current.includes(resourceId) ? current : [...current, resourceId]);
            }}
            onRemoveResource={(resourceId) => {
              setComposerResourceIds((current) => current.filter((id) => id !== resourceId));
            }}
            onOpenResource={openResource}
            onSelectZip={workspace?.scope ? undefined : (file) => {
              if (!file) return;

              const validationError = getCodebaseZipValidationError(file);

              if (validationError) {
                setComposerZipError(validationError);
                return;
              }

              setComposerZip(file);
              setComposerZipError('');
              setComposerRefs([]);
              setComposerResourceIds([]);
            }}
            onRemoveZip={() => {
              setComposerZip(null);
              setComposerZipError('');
            }}
            onOpenIssue={setSelectedIssueKey}
            onStop={activeChat && !composerZip ? () => {
              setIsSendingMessage(false);
              void cancelReply(activeChat.id).catch((error: unknown) => {
                console.error('Failed to cancel reply:', error);
              });
            } : undefined}
            onSend={async (content) => {
              if (composerZip) {
                return sendUploadedZip(composerZip, content);
              }

              // A question about attached issues is never a request to start a scan.
              const isSent = send(content, {
                chatId: activeChat?.id ?? null,
                issueRefs: composerRefs,
                resourceIds: composerResourceIds,
                detectIntent: composerRefs.length === 0 && composerResourceIds.length === 0
              });

              if (isSent) {
                setComposerRefs([]);
                setComposerResourceIds([]);
              }

              return isSent;
            }}
          />
        )}

        {view === 'issues' && (
          <AppIssuesView
            register={register}
            scanFilter={issuesScanFilter}
            selectedKey={selectedIssue?.key ?? null}
            onScanFilterChange={setIssuesScanFilter}
            onSelect={(key) => setSelectedIssueKey((current) => current === key ? null : key)}
            onSetStatus={async (keys, status) => {
              await setStatus(keys, status);
              setOverviewReloadKey((key) => key + 1);
            }}
            onAsk={askAboutIssues}
            onRunPentest={() => {
              openView('chat', workspaceId);
              send(ASSESSMENT_PROMPT, { chatId: null });
            }}
            onScanFinished={() => setIssuesReloadKey((key) => key + 1)}
            onRetry={reloadIssues}
          />
        )}
      </main>

      {selectedIssue && (
        <AppIssuePanel
          issue={selectedIssue}
          onClose={() => setSelectedIssueKey(null)}
          onSetStatus={async (status) => {
            await setStatus([selectedIssue.key], status);
            setOverviewReloadKey((key) => key + 1);
          }}
          onFix={() => fixIssue(selectedIssue)}
          onAsk={() => askAboutIssues([selectedIssue])}
        />
      )}

      <RunGitHubLoginModal
        isOpen={isLoginOpen}
        configState={account.githubConfigState === 'loading' ? 'loading' : 'ready'}
        isConfigured={Boolean(account.githubConfig?.configured)}
        onCloseLoginPrompt={closeLogin}
        onLogin={account.connectGitHub}
      />

      <SubscriptionCheckoutModal
        plan={subscriptionCheckout?.plan ?? null}
        isSubmitting={subscriptionCheckout?.isSubmitting ?? false}
        error={subscriptionCheckout?.error ?? ''}
        onCancel={() => setSubscriptionCheckout((current) => current?.isSubmitting ? current : null)}
        onError={(message) => setSubscriptionCheckout((current) => current && { ...current, error: message })}
        onSubmit={submitSubscription}
      />
    </div>
  );
}

export default AppPage;
